import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CalendarDays, Download, Home, Pencil, Send, Share2, Stamp, Tag, UserRound } from "lucide-react";
import client, { unwrap, apiErrorMessage } from "@/api/client";
import StatusBadge from "@/components/StatusBadge";
import QuotationItems from "@/components/quotation/QuotationItems";
import {
  ApprovalDecisionCard,
  CustomerProjectCard,
  DesignPreviewCard,
  FinalizeDialog,
  SharedLinkCard,
  ShareDialog,
  formatDay,
} from "@/components/quotation/QuotationSidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function quotationNumber(quotation) {
  if (!quotation) return "";
  const year = new Date(quotation.createdAt).getFullYear();
  const short = String(quotation._id).slice(-5).toUpperCase();
  return `HI-${year}-${short}`;
}

// Discount and GST can change until approval (QuotationsService.updatePricing).
const PRICING_EDITABLE_STATUSES = ["DRAFT", "CHANGES_REQUIRED", "SUBMITTED_FOR_APPROVAL"];
// Mirror the backend's ITEMS_EDITABLE_STATUSES and TERMS_EDITABLE_STATUSES.
const ITEMS_EDITABLE_STATUSES = ["DRAFT", "CHANGES_REQUIRED"];
const TERMS_EDITABLE_STATUSES = ["DRAFT", "CHANGES_REQUIRED", "SUBMITTED_FOR_APPROVAL", "APPROVED"];

const ACTION_TOASTS = {
  submit: "Submitted for approval",
  approve: "Quotation approved",
  "request-changes": "Changes requested",
  finalize: "Quotation finalized — PDF ready",
};

function Meta({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-foreground/85">
      <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      {children}
    </span>
  );
}

export default function QuotationDetailPage() {
  const { quotationId } = useParams();
  const { hasPermission } = useAuth();
  const itemsRef = useRef(null);
  const [publicLink, setPublicLink] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pricingEditing, setPricingEditing] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Absolute, so it can be pasted into WhatsApp or email as is.
  const publicQuoteUrl = publicLink ? `${window.location.origin}/quote/${publicLink}` : "";

  const { data, refetch } = useQuery({
    queryKey: ["quotation", quotationId],
    queryFn: () => client.get(`/quotations/${quotationId}`).then(unwrap),
  });
  const { data: tenant } = useQuery({ queryKey: ["tenant-me"], queryFn: () => client.get("/tenants/me").then(unwrap) });

  const quotation = data?.quotation;
  const items = useMemo(() => data?.items || [], [data]);
  const projectName = data?.projectName;
  const customerName = data?.customerName;
  const number = quotationNumber(quotation);
  const rooms = useMemo(() => [...new Set(items.map((i) => i.roomName))], [items]);
  const status = quotation?.status;

  useEffect(() => {
    if (status === "SHARED" && quotation?.publicToken && !publicLink) setPublicLink(quotation.publicToken);
  }, [status, quotation, publicLink]);

  // The PDF lives in storage behind a short-lived signed URL, fetched on click. The tab is opened
  // first, synchronously, so popup blockers treat it as a response to the click.
  const openPdf = async () => {
    const tab = window.open("", "_blank");
    setPdfLoading(true);
    try {
      const { url } = await client.get(`/files/${quotation.pdfFileId}`).then(unwrap);
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      tab?.close();
      toast.error(apiErrorMessage(err, "Couldn't open the PDF"));
    } finally {
      setPdfLoading(false);
    }
  };

  const makeActionOptions = (action, after) => ({
    mutationFn: (payload) => client.patch(`/quotations/${quotationId}/${action}`, payload || {}),
    onSuccess: (res) => {
      const result = res.data.data;
      if (action === "share") {
        setPublicLink(result.publicToken);
        toast.success(result.emailedTo ? `Quotation shared and emailed to ${result.emailedTo}` : "Quotation shared. No email on file, so copy the link.");
      } else {
        toast.success(ACTION_TOASTS[action]);
      }
      after?.();
      refetch();
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed"),
  });

  const submitMutation = useMutation(makeActionOptions("submit"));
  const approveMutation = useMutation(makeActionOptions("approve"));
  const requestChangesMutation = useMutation(makeActionOptions("request-changes"));
  const finalizeMutation = useMutation(makeActionOptions("finalize", () => setFinalizeOpen(false)));
  const shareMutation = useMutation(makeActionOptions("share"));

  const canApprove = status === "SUBMITTED_FOR_APPROVAL" && hasPermission("quotation:approve");
  const canEditPricing = hasPermission("quotation:create") && PRICING_EDITABLE_STATUSES.includes(status);
  const canEditItems = hasPermission("quotation:create") && ITEMS_EDITABLE_STATUSES.includes(status);
  const canEditNotes = hasPermission("quotation:create") && TERMS_EDITABLE_STATUSES.includes(status);

  // One primary action per status, in the spot the design gives "Share".
  const primary = (() => {
    if (["DRAFT", "CHANGES_REQUIRED"].includes(status) && hasPermission("quotation:create")) {
      return { label: "Submit for Approval", icon: Send, testId: "submit-quotation-button", onClick: () => submitMutation.mutate(), pending: submitMutation.isPending };
    }
    if (status === "APPROVED" && hasPermission("quotation:finalize")) {
      return { label: "Finalize", icon: Stamp, testId: "open-finalize-button", onClick: () => setFinalizeOpen(true) };
    }
    if (["FINALIZED", "SHARED"].includes(status) && hasPermission("quotation:share")) {
      return { label: "Share", icon: Share2, testId: "open-share-button", onClick: () => setShareOpen(true) };
    }
    return null;
  })();

  const location = [data?.projectBhk, data?.projectCity].filter(Boolean).join(", ");

  return (
    <div data-testid="quotation-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/quotations" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-3" data-testid="quotation-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Quotations / #{number}
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-3xl sm:text-[2.6rem] leading-tight text-foreground">{projectName}</h1>
            {quotation && <StatusBadge status={status} />}
          </div>
          <p className="text-lg text-muted-foreground mt-1">Prepared for {customerName}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 shrink-0">
          {canEditPricing && (
            <Button
              variant="outline"
              size="lg"
              data-testid="edit-quotation-button"
              className="bg-card"
              onClick={() => {
                setPricingEditing(true);
                itemsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
          <Button
            variant="outline"
            size="lg"
            className="bg-card"
            data-testid="download-pdf-button"
            disabled={!quotation?.pdfFileId || pdfLoading}
            title={quotation?.pdfFileId ? undefined : "The PDF is generated when the quotation is finalized"}
            onClick={openPdf}
          >
            <Download className="h-4 w-4 mr-2" /> {pdfLoading ? "Opening PDF…" : "Download PDF"}
          </Button>
          {primary && (
            <Button size="lg" data-testid={primary.testId} disabled={primary.pending} onClick={primary.onClick} className="bg-accent hover:bg-accent/90 text-accent-foreground px-6">
              <primary.icon className="h-4 w-4 mr-2" /> {primary.label}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
        {rooms.length > 0 && <Meta icon={Home}>{rooms.join(", ")}</Meta>}
        {customerName && <Meta icon={UserRound}>{customerName}</Meta>}
        {quotation && <Meta icon={CalendarDays}>{formatDay(quotation.createdAt)}</Meta>}
        {number && <Meta icon={Tag}><span className="font-medium">#{number}</span></Meta>}
      </div>

      {status === "CHANGES_REQUIRED" && quotation?.changeRequestNotes && (
        <div className="flex gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3" data-testid="change-request-banner">
          <AlertTriangle className="h-5 w-5 text-[hsl(41_60%_32%)] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Changes requested</p>
            <p className="text-sm text-foreground/80 whitespace-pre-line">{quotation.changeRequestNotes}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-start">
        <div className="min-w-0">
          {quotation && (
            <QuotationItems
              ref={itemsRef}
              quotation={quotation}
              items={items}
              projectId={quotation.projectId}
              canEditItems={canEditItems}
              canEditPricing={canEditPricing}
              canEditNotes={canEditNotes}
              pricingEditing={pricingEditing}
              setPricingEditing={setPricingEditing}
              onChanged={refetch}
            />
          )}
        </div>

        <div className="space-y-5">
          {canApprove && (
            <ApprovalDecisionCard
              quotation={quotation}
              items={items}
              isPending={approveMutation.isPending}
              onApprove={() => approveMutation.mutate()}
              onRequestChanges={(notes) => requestChangesMutation.mutate({ notes })}
            />
          )}
          {quotation && (
            <CustomerProjectCard customerName={customerName} location={location} date={formatDay(quotation.createdAt)} number={number} rooms={rooms} />
          )}
          <DesignPreviewCard items={items} />
          {publicQuoteUrl && <SharedLinkCard url={publicQuoteUrl} />}
        </div>
      </div>

      <FinalizeDialog
        open={finalizeOpen}
        setOpen={setFinalizeOpen}
        defaults={{ shopName: tenant?.shopName || "", customerName: customerName || "", projectName: projectName || "" }}
        pending={finalizeMutation.isPending}
        onSubmit={(form) => finalizeMutation.mutate(form)}
      />
      <ShareDialog
        open={shareOpen}
        setOpen={setShareOpen}
        status={status}
        defaultEmail={data?.customerEmail}
        publicUrl={publicQuoteUrl}
        customerPhone={data?.customerPhone}
        pending={shareMutation.isPending}
        onShare={(customerEmail) => shareMutation.mutate({ customerEmail })}
      />
    </div>
  );
}
